'use client';

import { Open, Swap } from '@velocity-exchange/icons';
import { PublicKey } from '@solana/web3.js';
import {
	BN,
	BigNum,
	MarketType,
	Order,
	OrderActionExplanation,
	PositionDirection,
	QUOTE_PRECISION_EXP,
	isSignedMsgOrder,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	UIMarket,
	UISerializableOrderActionRecord,
	abbreviateAddress,
	isEntirePositionOrder,
	matchEnum,
	roundBigNumToDecimalPlace,
	calculateAverageEntryPrice,
} from '@velocity-exchange/common';
import { useEffect, useMemo, useState } from 'react';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { getTradeInfoFromOrderActionRecord } from 'src/utils/tradeRecord';
import { getUrlForTx } from 'src/utils/explorer';
import useMarketsInfoStore from '../../stores/useMarketsInfoStore';
import TableV2 from '../Tables/TableV2';
import Counterparty from '../Counterparty';
import useShowAccountValues from 'src/hooks/useShowAccountValues';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { HistoryTableUIProps } from 'src/hooks/historyData/historyDataTypes';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import { HistoryType } from '../../@types/historyTables';
import { useTradesHistory } from 'src/hooks/historyData/useTradesHistory';
import { usePredictionTradesHistory } from 'src/hooks/historyData/usePredictionTradesHistory';
import { cj } from 'src/utils/classNames';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Link from 'src/components/primitives/Link/Link';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import MarketCellV2 from 'src/components/Tables/cells/MarketCellV2';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import Button from 'src/components/primitives/Button/Button';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';

const DEFAULT_EMPTY_ARRAY: UISerializableOrderActionRecord[] = [];

export type HistoryRowCommonProps = {
	baseAssetAmountFilled: BigNum;
	baseAssetSymbol: string;
	displayDatesAsTimestamp: boolean;
	fee: BigNum;
	isLong: boolean;
	isSpot: boolean;
	isPredictionMarket: boolean;
	marketName: string;
	quoteAssetAmountFilled: BigNum;
	showAccountValues: boolean;
	tableGrid: string;
	ts: BN;
	txSig: string;
	counterparty: string | undefined;
	marketType: string;
	actionExplanation: OrderActionExplanation;
	decimals: number;
	filler?: string;
	isTaker?: boolean;
};

const TradeHistoryRowActions = ({
	txSig,
	counterparty,
	marketType,
	actionExplanation,
	isTaker,
	isSignedMsgTrade,
}: {
	txSig: string;
	counterparty: string | undefined;
	marketType: string;
	actionExplanation: OrderActionExplanation;
	isTaker?: boolean;
	isSignedMsgTrade?: boolean;
}) => {
	return (
		<div className="flex items-center gap-x-md">
			<Link
				variant="secondary"
				className="-m-sm p-sm"
				href={getUrlForTx(txSig)}
				rightIcon="none"
				openInNewTab
			>
				<Open size={16} />
			</Link>
			<Counterparty
				counterparty={counterparty ?? ''}
				marketType={ENUM_UTILS.toObj(marketType)}
				actionExplanation={actionExplanation}
				isTaker={isTaker}
				isSignedMsgTrade={isSignedMsgTrade}
			/>
		</div>
	);
};

const TABLE_GRID =
	'minmax(140px,8fr) 150px 200px minmax(112px,200px) minmax(100px,140px) minmax(100px,140px) minmax(120px,140px) minmax(80px,1fr)';

const HEADERS = [
	'time',
	'market',
	'direction',
	'size',
	'notional',
	'price',
	'fee',
	'',
];

interface OverviewTradeHistoryRowProps extends HistoryRowCommonProps {
	avgEntryPrice: string;
	isDataRow?: boolean;
	isSignedMsgTrade: boolean;
}

const OverviewTradeHistoryRow = (props: OverviewTradeHistoryRowProps) => {
	return (
		<TableV2.BodyRow
			className="h-10 border-0"
			grid={props.tableGrid}
			lastColumnJustify="start"
			isDataRow={props.isDataRow}
		>
			<TableV2.DateCell
				className="text-left"
				date={new Date(props.ts.toNumber() * 1000)}
				displayAsTimestamp={props.displayDatesAsTimestamp}
			/>

			<TableV2.MarketCell
				baseAssetSymbol={props.baseAssetSymbol}
				marketSymbol={props.marketName}
				isSpot={props.isSpot}
				className="w-auto gap-x-xs whitespace-nowrap"
			/>

			<TableV2.BodyCell
				className={cj(
					props.isLong
						? 'text-text-positive-green-button'
						: 'text-text-negative-red-button'
				)}
			>
				{props.isSpot || props.isPredictionMarket
					? props.isLong
						? 'Buy'
						: 'Sell'
					: props.isLong
						? 'Long'
						: 'Short'}{' '}
				{matchEnum(
					props.actionExplanation,
					OrderActionExplanation.LIQUIDATION
				) &&
					(props.isTaker || !props.filler) && (
						<>({props.isTaker ? 'Liquidated' : 'Liquidator'})</>
					)}
			</TableV2.BodyCell>

			<TableV2.TradeSizeCell
				baseSize={roundBigNumToDecimalPlace(
					props.baseAssetAmountFilled,
					props.decimals
				)}
				baseSymbol={props.baseAssetSymbol}
				marketSymbol={props.marketName}
				hideValues={!props.showAccountValues}
			/>

			<TableV2.NotionalCell
				value={props.quoteAssetAmountFilled}
				isAccountValueToHide
			/>

			<TableV2.BodyCell>{props.avgEntryPrice}</TableV2.BodyCell>

			<TableV2.AssetCell
				value={props.fee ? props.fee : BigNum.zero()}
				suffix="USDC"
				isAccountValueToHide
				disableTooltip
			/>

			<TableV2.BodyCell className="w-full justify-end">
				<TradeHistoryRowActions
					txSig={props.txSig}
					counterparty={props.counterparty}
					marketType={props.marketType}
					actionExplanation={props.actionExplanation}
					isTaker={props.isTaker}
					isSignedMsgTrade={props.isSignedMsgTrade}
				/>
			</TableV2.BodyCell>
		</TableV2.BodyRow>
	);
};

const V2TradeHistoryTableDisplay = (props: {
	page: 'trade' | 'portfolio' | 'predictions';
	historyTableUiProps: HistoryTableUIProps<UISerializableOrderActionRecord>;
	isLive?: boolean;
}) => {
	const isMobileScreenSize = useIsMobileScreenSize();

	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const [isTableScrollable, setIsTableScrollable] = useState(false);

	const getDecimalsForMarket = (
		marketIndex: number,
		marketType: MarketType
	) => {
		const marketInfo = getMarketInfoByIndexAndType(marketIndex, marketType);

		return marketInfo?.genericInfo.baseDisplayDecimals ?? 0;
	};

	// # Constants
	const pageSize = props.page == 'trade' ? 10 : 20;
	const historyType: HistoryType =
		props.page == 'predictions' ? 'predictionTrades' : 'trades';

	const currentAccount = useDriftAccountStore((s) => {
		const currentAccountKey = s.currentUserKey;
		const currentAccount = s.accounts[currentAccountKey ?? ''];

		return !!currentAccount;
	});
	const currentAccountPubkey = useDriftAccountStore((s) => {
		const currentAccountKey = s.currentUserKey;
		const currentAccount = s.accounts[currentAccountKey ?? ''];

		return currentAccount?.pubKey;
	});

	const setHistoryTableFilterState = useHistoryTableFilterStore((s) => s.set);
	const filters = useHistoryTableFilterStore((s) =>
		s.getFilterState(historyType)
	);
	const currentPage = filters.currentUiPage;

	// Use history data from hook instead of Zustand store
	const { historyData } = props.historyTableUiProps;
	const accountTradeHistory = historyData.records;
	const historyIsLoading = historyData.loading;
	const totalCount = historyData.totalCount;

	const filteredTrades = useMemo(
		() =>
			props.historyTableUiProps.filterFn
				? (accountTradeHistory?.filter((record) =>
						props.historyTableUiProps.filterFn(
							record as unknown as UISerializableOrderActionRecord,
							filters,
							{ isLive: props.isLive }
						)
					) as UISerializableOrderActionRecord[])
				: (accountTradeHistory as UISerializableOrderActionRecord[]),
		[accountTradeHistory, props.historyTableUiProps.filterFn, filters]
	);

	const { displayDatesAsTimestamp, toggleDateDisplayType } =
		useDateDisplayState();

	const [currentTradeHistory, setCurrentTradeHistory] = useState<
		UISerializableOrderActionRecord[]
	>([]);

	const maxNumOfRecordsToDisplay =
		filters.getFilterMode() === 'SHORT_TERM'
			? (totalCount ?? 0) // short term data API returns pages of 20 records up to the current page, and we do a workaround to (+1 to total count) when we know there is a next page
			: (filteredTrades?.length ?? 0); // long term data API returns up to 5000 records for the first page, so we check the actual displayed records (filtered records) length

	const showAccountValues = useShowAccountValues();

	useEffect(() => {
		setCurrentTradeHistory(
			filteredTrades?.slice(
				currentPage * pageSize,
				(currentPage + 1) * pageSize
			) ?? DEFAULT_EMPTY_ARRAY
		);
	}, [filteredTrades, currentPage]);

	const resetState = () => {
		setCurrentTradeHistory(DEFAULT_EMPTY_ARRAY);
		setHistoryTableFilterState((s) => {
			s.trades.currentUiPage = 0;
		});
	};

	const isConnected = useWalletIsConnected({
		onDisconnectedCallback: resetState,
	});

	const [amountDisplayType, setAmountDisplayType] = useState<
		'size' | 'notional'
	>('size');

	const handleToggleAmountDisplayType = () => {
		setAmountDisplayType(amountDisplayType === 'size' ? 'notional' : 'size');
	};

	return (
		<HistoryTable
			tableGrid={TABLE_GRID}
			pageSize={pageSize}
			currentPage={currentPage}
			pageChangeCallback={(newPage) =>
				props.historyTableUiProps.executePageChange(newPage, pageSize)
			}
			totalItems={maxNumOfRecordsToDisplay}
			loading={historyIsLoading ?? false}
			tableState={{
				records: currentTradeHistory,
				id: 'trade_history_table',
			}}
			setIsScrollable={setIsTableScrollable}
			tableContentTop={
				<TableV2.HeaderRow
					grid={TABLE_GRID}
					className="w-full"
					lastColumnJustify="start"
					addScrollPadding={isTableScrollable}
				>
					{HEADERS.map((label, index) => {
						return (
							<TableV2.HeaderCell
								key={`deposit_header_${index}`}
								className="gap-x-xs"
							>
								{label}
								{label.toLowerCase() === 'time' && (
									<IconButton
										size="sm"
										variant="tertiary"
										className="-m-md p-md"
										icon={Swap}
										onClick={toggleDateDisplayType}
									/>
								)}
							</TableV2.HeaderCell>
						);
					})}
				</TableV2.HeaderRow>
			}
			tableContentMiddle={
				isConnected &&
				currentAccount &&
				currentTradeHistory?.map((tradeRecord, index) => {
					const tradeInfo = getTradeInfoFromOrderActionRecord(
						currentAccountPubkey ?? PublicKey.default,
						tradeRecord
					);
					const avgEntryPrice = calculateAverageEntryPrice(
						tradeInfo.quoteAssetAmountFilled,
						tradeInfo.baseAssetAmountFilled
					).toNotional(true);
					const uiMarket = new UIMarket(
						tradeInfo.marketIndex,
						ENUM_UTILS.toObj(tradeInfo.marketType)
					);
					const marketName = uiMarket.uiSymbols.marketDisplaySymbol;

					const baseAssetSymbol = uiMarket.uiSymbols.baseAssetSymbol;
					const isSpot = uiMarket.isSpot;
					const isLong = matchEnum(tradeInfo.direction, PositionDirection.LONG);
					const isPredictionMarket = uiMarket.isPredictionMarket;

					const decimals = getDecimalsForMarket(
						tradeInfo.marketIndex,
						ENUM_UTILS.toObj(tradeInfo.marketType)
					);

					const isSignedMsgTrade = isSignedMsgOrder(
						tradeRecord as unknown as Order
					);

					const key = `${index}_${tradeInfo.marketIndex.toString()}_${tradeInfo.txSig?.toString()}`;

					const isEntirePosition = isEntirePositionOrder(
						tradeInfo.baseAssetAmountFilled
					);

					const amountText = (() => {
						if (amountDisplayType === 'size') {
							return tradeInfo.baseAssetAmountFilled.prettyPrint();
						}

						if (!showAccountValues) return '∗∗∗∗∗';

						if (isEntirePosition) {
							return 'Entire Position';
						}

						return tradeInfo.quoteAssetAmountFilled.toNotional();
					})();

					const filledBy = (() => {
						if (tradeInfo.counterparty) {
							return (
								<Link
									variant="primary"
									href={`/portfolio/trade?userAccount=${tradeInfo.counterparty.toString()}`}
									openInNewTab
								>
									{abbreviateAddress(tradeInfo.counterparty.toString())}
								</Link>
							);
						}

						if (
							!isSpot &&
							ENUM_UTILS.match(
								tradeRecord.actionExplanation,
								OrderActionExplanation.ORDER_FILLED_WITH_AMM
							)
						) {
							return 'Drift AMM';
						}

						// External DEX integrations removed in velocity migration.
						return 'Drift';
					})();

					return isMobileScreenSize ? (
						<MobileTableCard.Root key={key} className="gap-y-lg">
							<MarketCellV2
								className="py-0"
								direction={isLong ? 'long' : 'short'}
								marketSymbol={marketName}
								marketIndex={tradeInfo.marketIndex}
								marketType={ENUM_UTILS.toObj(tradeInfo.marketType)}
								textClass="typo-label-md"
								hideLeverage
							/>
							<div className="grid w-full grid-cols-3">
								<MobileTableCard.LabelWithValue
									label={
										<>
											{amountDisplayType === 'notional' ? 'Value' : 'Size'}
											{!isEntirePosition && tradeInfo.baseAssetAmountFilled && (
												<IconButton
													size="sm"
													variant="tertiary"
													className="-m-sm p-sm"
													onClick={handleToggleAmountDisplayType}
													icon={Swap}
												/>
											)}
										</>
									}
									value={amountText}
								/>
								<MobileTableCard.LabelWithValue
									className="items-center"
									label="Price"
									value={avgEntryPrice}
								/>
								<MobileTableCard.LabelWithValue
									className="items-end"
									label="Fee"
									value={
										<NumberDisplay
											value={tradeInfo.fee}
											type="currency"
											isAccountValueToHide
										/>
									}
								/>
							</div>
							<div className="grid grid-cols-2">
								<MobileTableCard.LabelWithValue
									label={
										<>
											Time
											<IconButton
												size="sm"
												variant="tertiary"
												className="-m-md p-md"
												onClick={toggleDateDisplayType}
												icon={Swap}
											/>
										</>
									}
									value={
										<TableV2.DateCell
											className="w-fit py-0 whitespace-nowrap"
											displayAsTimestamp={displayDatesAsTimestamp}
											date={new Date(tradeInfo.ts.toNumber() * 1000)}
										/>
									}
								/>
								<MobileTableCard.LabelWithValue
									className="items-end"
									label="Filled by"
									value={filledBy}
								/>
							</div>
							{tradeInfo.txSig && (
								<Button
									size="sm"
									variant="secondary"
									as="a"
									href={getUrlForTx(tradeInfo.txSig)}
									openInNewTab
								>
									View in scanner
								</Button>
							)}
						</MobileTableCard.Root>
					) : (
						<OverviewTradeHistoryRow
							key={key}
							avgEntryPrice={avgEntryPrice}
							baseAssetAmountFilled={tradeInfo.baseAssetAmountFilled}
							baseAssetSymbol={baseAssetSymbol}
							displayDatesAsTimestamp={displayDatesAsTimestamp}
							fee={tradeInfo.fee ?? BigNum.zero(QUOTE_PRECISION_EXP)}
							isLong={isLong}
							isSpot={isSpot}
							marketName={marketName}
							quoteAssetAmountFilled={tradeInfo.quoteAssetAmountFilled}
							showAccountValues={showAccountValues}
							tableGrid={TABLE_GRID}
							ts={tradeInfo.ts}
							isDataRow
							txSig={tradeRecord.txSig}
							counterparty={tradeInfo.counterparty?.toString()}
							marketType={tradeInfo.marketType}
							actionExplanation={tradeRecord.actionExplanation}
							isTaker={tradeInfo.isTaker}
							isPredictionMarket={isPredictionMarket}
							decimals={decimals}
							isSignedMsgTrade={isSignedMsgTrade}
							filler={tradeRecord.filler?.toString()}
						/>
					);
				})
			}
		/>
	);
};

const TradesTableInner = (props: {
	page: 'trade' | 'portfolio';
	isLive?: boolean;
}) => {
	const tableState = useTradesHistory();

	return (
		<V2TradeHistoryTableDisplay
			page={props.page}
			historyTableUiProps={tableState}
			isLive={props.isLive}
		/>
	);
};

const PredictionTradesTableInner = (props: { isLive?: boolean }) => {
	const tableState = usePredictionTradesHistory();

	return (
		<V2TradeHistoryTableDisplay
			page="predictions"
			historyTableUiProps={tableState}
			isLive={props.isLive}
		/>
	);
};

const V2TradeHistoryTable = (props: {
	page: 'trade' | 'portfolio' | 'predictions';
	isLive?: boolean;
}) => {
	if (props.page === 'predictions') {
		return <PredictionTradesTableInner isLive={props.isLive} />;
	}
	return <TradesTableInner page={props.page} isLive={props.isLive} />;
};

export default V2TradeHistoryTable;
